import React from 'react';
import { CommandBarButton, ConstrainMode, DetailsList, DetailsListLayoutMode, MessageBar, MessageBarType, PrimaryButton, SelectionMode } from '@fluentui/react';
import { WhitelistingType } from "../../GlobalConst/globalEnums";
import { mapEnumToOptions, renderDate } from '../../helpers/HelperMethods';
import Person from '../EditSite/Person';
import { useState, useEffect } from 'react';
import Api from '../../services/Api';
import LoadingIcon from '../formFields/LoadingIcon';
import { mapDbUserForPeoplePicker } from '../formFields/PeoplePicker';
import CustomDropDown from '../EditSite/SiteEditElements/CustomDropDown';
import CustomTextField from '../EditSite/SiteEditElements/CustomTextField';

const PowerPlatformWhitelisting = () => {

    const [newItems, setNewItems] = useState([])
    const [items, setItems] = useState([])
    const [message, setMessage] = useState()
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [childrenValidation, setChildrenValidation] = useState({})
    const [isChildrenUpdateValid, setIsChildrenUpdateValid] = useState(true);

    useEffect(() => {
        getWhitelistingItems()
    }, [])

    useEffect(() => {
        setIsChildrenUpdateValid(Object.keys(childrenValidation).reduce((value, key) => {
            return value && childrenValidation[key];
        }, true))
    }, [childrenValidation])

    const onUpdateChildrenValidation = (key, isValid) => {
        setChildrenValidation(valid => ({ ...valid, [key]: isValid }))
    }

    const getWhitelistingItems = () => {
        setIsLoading(true);
        setMessage()
        Api.get(`/Settings/PowerAppWhitelisting`)
            .then(res => res.data)
            .then(data => {
                setItems(data.map(x => ({...x, requestedBy: mapDbUserForPeoplePicker(x.requestedBy)})))
                setIsLoading(false)
            })
            .catch(error => {
                setIsSaving(false);
                console.log(error); 
                setMessage({
                    type: MessageBarType.error,
                    text: 'Cannot get settings from database: ' + error
                });
            });
    }
    
    const saveItems = () => {
        setIsSaving(true);
        setMessage();

        const data = [...items, ...newItems.filter(x => !x.remove)]

        Api.post(`/Settings/PowerAppWhitelisting`, data)
            .then(res => res.data)
            .then(() => {
                setNewItems([]);
                setIsSaving(false);
                getWhitelistingItems();
                setMessage({
                    type: MessageBarType.success,
                    text: 'Settings have been saved.'
                });
            })
            .catch(error => {
                setIsSaving(false);
                console.log(error); 
                setMessage({
                    type: MessageBarType.error,
                    text: 'Cannot save settings: ' + error
                });
            });
    }

    const setValue = (id, key, value, isAdd) => {
        if (isAdd) {
            let item = newItems.find(x => x.id === id)
            item[key] = value;
            setNewItems([...newItems])
        }
        else {
            let item = items.find(x => x.id === id)
            item[key] = value;
            setItems([...items])
        }

        if (key === 'remove') {
            onUpdateChildrenValidation(`${id}_${isAdd}_type`, true)
            onUpdateChildrenValidation(`${id}_${isAdd}_name`, true)
        }
    }

    const typeOptions = mapEnumToOptions(WhitelistingType)

    const columns: IColumn[] = [
         {
            key: 'type',
            name: 'Type',
            minWidth: 100, 
            maxWidth: 300,
            isResizable: true,
            onRender: (item) => 
                <CustomDropDown
                    options={item.isReadOnly ? typeOptions : typeOptions.filter(x => x.key !== 'Created By')}
                    defaultValue={item.type}
                    onUpdate={(key) => setValue(item.id, 'type', key, item.add)}
                    required={true}
                    onValidation={(v) => onUpdateChildrenValidation(`${item.id}_${item.add}_type`, v)}
                    disabled={item.isReadOnly || item.remove}
                />
        },
        {
            key: 'name',
            name: 'Name',
            minWidth: 100, 
            maxWidth: 400,
            isResizable: true,
            onRender: (item) =>
                <CustomTextField
                    defaultValue={item.name} 
                    onUpdate={(value) => setValue(item.id, 'name', value.trim(), item.add)} 
                    required={true}
                    onValidation={(v) => onUpdateChildrenValidation(`${item.id}_${item.add}_name`, v)}
                    disabled={item.isReadOnly || item.remove}
                />
        },
        {
            key: 'requestedBy',
            name: 'Requested By',
            minWidth: 200, 
            maxWidth: 300,
            isResizable: true,
            onRender: (item) =>
                <Person
                    onChange={(value) => setValue(item.id, 'requestedBy', value.length ? value[0] : null, item.add)}
                    user={item.requestedBy}
                    isRequired={false}
                    limited={true}
                    disabled={item.isReadOnly || item.remove}
                />
        },
        {
            key: 'description',
            name: 'Comment',
            minWidth: 200, 
            maxWidth: 600,
            isResizable: true,
            onRender: (item) =>
                <CustomTextField
                    defaultValue={item.description} 
                    onUpdate={(value) => setValue(item.id, 'description', value.trim(), item.add)} 
                    multiline={true}
                    disabled={item.isReadOnly || item.remove}
                />
        },
        {
            key: 'created',
            name: 'Created',
            minWidth: 100, 
            maxWidth: 100,
            isResizable: true,
            onRender: (item) => <span style={{fontSize: 14}}>{renderDate(item.created)}</span>
        },
        {
            key: 'actions',
            minWidth: 100, 
            maxWidth: 100, 
            isResizable: true,
            onRender: (item) => 
                item.remove
                ? <span>Save changes to confirm removal</span>
                : (!item.isReadOnly && <CommandBarButton styles={{root: {backgroundColor: 'inherit'}}} iconProps={{iconName: 'Cancel', styles: {root: {color: 'red'}}}} onClick={() => setValue(item.id, 'remove', true, item.add)} text='Remove Item' />)
        }
    ]

    const newItemBtn = <CommandBarButton styles={{root: {marginTop: 10}}} iconProps={{ iconName: 'Add' }} text="Add New Item" onClick={() => {setNewItems([...newItems, { id: newItems.length + 1, add: true }])}} />
    const saveBtn =  isSaving
                    ? <LoadingIcon defaultHeight={true} />
                    : <PrimaryButton text="Save all changes" onClick={saveItems} disabled={!isChildrenUpdateValid} />
    const msg = message && 
                <MessageBar messageBarType={message.type}>
                    {message.text}
                </MessageBar>

    const allItems = [...items, ...newItems.filter(x => !x.remove)]

    const list =
        <div>
            <DetailsList 
                items={allItems}
                columns={columns}
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible={true}
                selectionMode={SelectionMode.none}
                styles={listStyles}
                constrainMode={ConstrainMode.horizontalConstrained}
            />
            {newItemBtn}
            <div style={{marginTop: 40, display: 'flex', gap: 20, flexWrap: 'wrap'}}>
                {saveBtn}
                {msg}
            </div>
        </div>

    return (
        <div>
            {
                isLoading
                    ? <LoadingIcon />
                    : list
            }
        </div>
    )
}

const listStyles = {
    root: {
        width: '100%'
    }
}

export default PowerPlatformWhitelisting


// WEBPACK FOOTER //
// ./src/components/Settings/PowerPlatformWhitelisting.js